import { TFunction } from "i18next";
import ReviewAndSubmitStep from "./WizardSteps/ReviewAndSubmitStep";
import { Workspace } from "../../types/workspace";
import formModel from "./formModel";
import { Anonymity, StorageRegion } from "../../types/enums";
import * as Yup from "yup";
import DefineActivitiesStep from "./WizardSteps/DefineActivitiesStep";
import NameAndLocationStep from "./WizardSteps/NameAndLocationStep";
import ChooseLCStep from "./WizardSteps/ChooseLCStep";
import { validateLC } from "../../api/user";

const {
  formField: {
    tenantName,
    projectName,
    description,
    industry,
    location,
    storageRegion,
    anonymity,
    deviceType,
    localCoordinatorEmail,
    confirmEmail,
    skipLocalCoordinator,
    activities,
    skipActivities,
    readyForReview,
  },
} = formModel;

const getReviewAndSubmitStep = (t: TFunction) => {
  return {
    label: t("labels.wizardStep7"),
    component: <ReviewAndSubmitStep />,
  };
};

const defineActivitesStep = (t: TFunction) => {
  return {
    label: t("labels.wizardStep5"),
    component: <DefineActivitiesStep />,
    validationScheme: Yup.object().shape({
      [skipActivities.name]: Yup.boolean(),
      [activities.name]: Yup.array()
        .of(
          Yup.object({
            activity: Yup.string().required("errors.required"),
          })
        )
        //@ts-ignore
        .unique("errors.duplicate", (a: any) => a.activity)
        .when(skipActivities.name, (_skipActivities: boolean) => {
          if (!_skipActivities) {
            return (
              Yup.array()
                .of(
                  Yup.object({
                    activity: Yup.string().required("errors.required"),
                  })
                )
                .min(1, "errors.minActivities")
                //@ts-ignore
                .unique("errors.duplicate", (a: Activity) => a.activity)
            );
          }
        }),
    }),
  };
};

Yup.addMethod(Yup.array, "unique", function (message, mapper = (a: any) => a) {
  return this.test("unique", message, function (list) {
    let shouldSkip = this.options?.context?.skipAssignments; // this.from[0].value.skipAssignments;
    if (shouldSkip) {
      return true;
    } else {
      return list?.length === new Set(list?.map(mapper)).size;
    }
  });
});

const createProjectStep = (t: TFunction) => {
  return {
    stepNum: 1,
    label: t("labels.wizardStep1"),
    component: <NameAndLocationStep />,
    validationScheme: Yup.object().shape({
      [projectName.name]: Yup.string()
        .min(2, "errors.min2")
        .max(64, "errors.max64")
        .required("errors.required"),
      [description.name]: Yup.string().max(400, "errors.max400"),
      [industry.name]: Yup.string().nullable().required("errors.required"),
      [location.name]: Yup.string().nullable().required("errors.required"),
      [anonymity.name]: Yup.string().nullable().required("errors.required"),
      [deviceType.name]: Yup.array().min(1, "errors.required").max(1, "Sorry, we dont support multiple device types at the moment"),
    }),
  };
};

const chooseLCStep = (t: TFunction) => {
  return {
    label: t("labels.wizardStep3"),
    component: <ChooseLCStep />,
    validationScheme: Yup.object().shape({
      [skipLocalCoordinator.name]: Yup.boolean(),
      [localCoordinatorEmail.name]: Yup.string().when(
        skipLocalCoordinator.name,
        //@ts-ignore
        (canSkip) => {
          if (!canSkip)
            return Yup.string()
              .email("errors.invalid")
              .required("errors.required")
              .test("isEmailValid", "errors.invalid", async function (value) {
                var email = Yup.string().email();
                var test = await email.validate(value);
                if (value) {
                  if (test) {
                    try {
                      var workspaceName = this.parent.tenantName;
                      var id = this.parent.workspaceId
                      var res = await validateLC({
                        email: value,
                        tenantName: workspaceName,
                        workspaceId : id
                      });

                      return res;
                    } catch (e) {
                      return false;
                    }
                  } else {
                    return true;
                  }
                }
              });
        }
      ),
      [confirmEmail.name]: Yup.string().when(
        skipLocalCoordinator.name,
        //@ts-ignore
        (canSkip) => {
          if (!canSkip)
            return Yup.string().test(
              "email-match",
              "errors.emailNoMatch",
              function (value) {
                return this.parent.localCoordinatorEmail === value;
              }
            );
        }
      ),
    }),
  };
};

export function GetWizard(activeWorkspace: Workspace, t: TFunction) {
  let initialValues = {
    workspaceId: activeWorkspace.workspaceId,
    [tenantName.name]: activeWorkspace.tenantName,
    [projectName.name]: "",
    [description.name]: "",
    [industry.name]: "",
    [location.name]: "",
    [storageRegion.name]: StorageRegion.EU,
    [anonymity.name]:
      activeWorkspace.anonymity === Anonymity.Known
        ? Anonymity.Known
        : Anonymity.Unknown,
    [deviceType.name]:
      activeWorkspace.availableDeviceTypes.length === 1
        ? activeWorkspace.availableDeviceTypes
        : [],
    [localCoordinatorEmail.name]: "",
    [confirmEmail.name]: "",
    [skipLocalCoordinator.name]: false,
    [activities.name]: [],
    [skipActivities.name]: false,
    [readyForReview.name]: false,
  };
  let steps = [];

  steps.push(createProjectStep(t));
  steps.push(chooseLCStep(t));
  steps.push(defineActivitesStep(t));
  steps.push(getReviewAndSubmitStep(t));
  return { initialValues, steps };
}